/**
 * Pre-Flight Validator View Component
 * Displays validation results - purely informational, no fix buttons
 * Data generation is controlled via configuration and happens during simulation
 */

import React from 'react';
import {
  CheckCircle, XCircle, AlertTriangle, Info, Zap, Database, Package, Truck
} from 'lucide-react';
import { PreFlightReport, PreFlightValidation, ValidationCategory } from '../types';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface PreFlightValidatorViewProps {
  report: PreFlightReport;
}

export const PreFlightValidatorView: React.FC<PreFlightValidatorViewProps> = ({
  report
}) => {
  const { t } = useTranslation();
  const groupedValidations = groupByCategory(report.validations);

  // Count issues that can be auto-generated
  const missingOrders = report.validations.find(v => v.name === 'Salgsordrer tilgjengelig')?.status !== 'pass';
  const missingPOrders = report.validations.find(v => v.name === 'Innkjøpsordrer tilgjengelig')?.status !== 'pass';
  const missingStock = report.validations.find(v => v.name === 'Lagerbeholdning')?.status !== 'pass';
  const missingPLists = report.validations.find(v => v.name === 'Plukklister tilgjengelig')?.status !== 'pass';

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header Card */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl shadow-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">{t('manager.modules.simulator.preflight.title', {}, 'System-Sjekk')}</h2>
            <p className="text-blue-100 mt-1">{t('manager.modules.simulator.preflight.subtitle', {}, 'Verifiserer forutsetninger for simulering')}</p>
          </div>
          <div className={`px-5 py-3 rounded-xl font-bold text-lg ${
            report.canStart
              ? 'bg-green-500 text-white'
              : 'bg-red-500 text-white'
          }`}>
            {report.canStart
              ? t('manager.modules.simulator.preflight.ready', {}, '✓ Klar')
              : t('manager.modules.simulator.preflight.blocked', {}, '✗ Blokkert')}
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 mt-6">
          <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
            <CheckCircle className="w-8 h-8 text-green-300 mx-auto mb-2" />
            <p className="text-3xl font-bold">{report.passed}</p>
            <p className="text-sm text-blue-200">{t('manager.modules.simulator.preflight.passed', {}, 'Bestått')}</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
            <AlertTriangle className="w-8 h-8 text-yellow-300 mx-auto mb-2" />
            <p className="text-3xl font-bold">{report.warnings}</p>
            <p className="text-sm text-blue-200">{t('manager.modules.simulator.preflight.warnings', {}, 'Advarsler')}</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
            <XCircle className="w-8 h-8 text-red-300 mx-auto mb-2" />
            <p className="text-3xl font-bold">{report.criticalErrors}</p>
            <p className="text-sm text-blue-200">{t('manager.modules.simulator.preflight.critical', {}, 'Kritiske')}</p>
          </div>
        </div>
      </div>

      {/* Auto-Generation Info Box */}
      {(missingOrders || missingPOrders || missingStock || missingPLists) && report.canStart && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-amber-100 rounded-lg">
              <Zap className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-amber-800 text-lg">{t('manager.modules.simulator.preflight.autoGenTitle', {}, 'Auto-Generering Tilgjengelig')}</h3>
              <p className="text-amber-700 mt-1 text-sm">
                {t('manager.modules.simulator.preflight.autoGenDescription', {}, 'Følgende data mangler, men kan genereres automatisk under simulering:')}
              </p>
              <div className="flex flex-wrap gap-2 mt-3">
                {missingOrders && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                    <Package className="w-4 h-4" />
                    {t('manager.modules.simulator.preflight.salesOrders', {}, 'Salgsordrer')}
                  </span>
                )}
                {missingPOrders && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                    <Truck className="w-4 h-4" />
                    {t('manager.modules.simulator.preflight.purchaseOrders', {}, 'Innkjøpsordrer')}
                  </span>
                )}
                {missingStock && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                    <Database className="w-4 h-4" />
                    {t('manager.modules.simulator.preflight.stockLevel', {}, 'Lagerbeholdning')}
                  </span>
                )}
                {missingPLists && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                    {t('manager.modules.simulator.preflight.pickingLists', {}, 'Plukklister')}
                  </span>
                )}
              </div>
              <p className="text-amber-600 mt-3 text-sm">
                {t('manager.modules.simulator.preflight.configureCountPrefix', {}, 'Konfigurer antall i')} <strong>{t('manager.modules.simulator.header.settings', {}, 'Innstillinger')}</strong> {t('manager.modules.simulator.preflight.configureCountSuffix', {}, 'før du starter.')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Validation Groups */}
      {Object.entries(groupedValidations).map(([category, validations]) => (
        validations.length > 0 && (
          <ValidationGroup
            key={category}
            category={category as ValidationCategory}
            validations={validations}
          />
        )
      ))}

      {/* Multi-user tip */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Info className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="font-bold text-blue-800">{t('manager.modules.simulator.preflight.multiUserTitle', {}, 'Multi-Bruker Observasjon')}</h3>
            <p className="text-blue-700 mt-1 text-sm">
              {t('manager.modules.simulator.preflight.multiUserText', {}, 'Under simulering kan andre brukere logge inn og se endringene i sanntid via de vanlige modulene (plukk, pakk, mottak, lagertelling, etc.). Alle endringer gjøres mot ekte data i databasen.')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Validation Group Component
interface ValidationGroupProps {
  category: ValidationCategory;
  validations: PreFlightValidation[];
}

const ValidationGroup: React.FC<ValidationGroupProps> = ({
  category,
  validations
}) => {
  const { t } = useTranslation();
  const categoryConfig: Record<ValidationCategory, { color: string; bgGradient: string; label: string; icon: React.ElementType }> = {
    'KRITISK': {
      color: 'red',
      bgGradient: 'from-red-50 to-red-100',
      label: t('manager.modules.simulator.preflight.categoryCritical', {}, 'Kritiske Krav'),
      icon: XCircle
    },
    'PÅKREVD': {
      color: 'orange',
      bgGradient: 'from-orange-50 to-orange-100',
      label: t('manager.modules.simulator.preflight.categoryRequired', {}, 'Påkrevde Krav'),
      icon: AlertTriangle
    },
    'ANBEFALT': {
      color: 'yellow',
      bgGradient: 'from-yellow-50 to-amber-100',
      label: t('manager.modules.simulator.preflight.categoryRecommended', {}, 'Anbefalte Krav'),
      icon: Info
    },
    'VALGFRITT': {
      color: 'blue',
      bgGradient: 'from-blue-50 to-indigo-100',
      label: t('manager.modules.simulator.preflight.categoryWorkflowData', {}, 'Workflow-Data'),
      icon: Database
    }
  };

  const config = categoryConfig[category];
  const Icon = config.icon;

  // Get color classes based on category
  const getColorClasses = (color: string) => {
    const colors: Record<string, { header: string; icon: string; border: string }> = {
      red: { header: 'bg-red-600', icon: 'text-white', border: 'border-red-200' },
      orange: { header: 'bg-orange-500', icon: 'text-white', border: 'border-orange-200' },
      yellow: { header: 'bg-amber-500', icon: 'text-white', border: 'border-amber-200' },
      blue: { header: 'bg-blue-600', icon: 'text-white', border: 'border-blue-200' }
    };
    return colors[color] || colors.blue;
  };

  const colorClasses = getColorClasses(config.color);

  return (
    <div className={`bg-white rounded-xl shadow-lg overflow-hidden border ${colorClasses.border}`}>
      <div className={`px-5 py-3 ${colorClasses.header}`}>
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${colorClasses.icon}`} />
          <h3 className="font-semibold text-white">{config.label}</h3>
          <span className="ml-auto bg-white/20 px-2 py-0.5 rounded text-sm text-white">
            {validations.filter(v => v.status === 'pass').length}/{validations.length}
          </span>
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {validations.map((validation, index) => (
          <ValidationItem
            key={index}
            validation={validation}
          />
        ))}
      </div>
    </div>
  );
};

// Validation Item Component
interface ValidationItemProps {
  validation: PreFlightValidation;
}

const ValidationItem: React.FC<ValidationItemProps> = ({ validation }) => {
  const { t } = useTranslation();
  const statusConfig = {
    pass: { icon: CheckCircle, color: 'text-green-500', bg: 'bg-green-50', badge: 'bg-green-100 text-green-700' },
    fail: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50', badge: 'bg-red-100 text-red-700' },
    warning: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50', badge: 'bg-amber-100 text-amber-700' },
    info: { icon: Info, color: 'text-blue-500', bg: 'bg-blue-50', badge: 'bg-blue-100 text-blue-700' }
  };

  const config = statusConfig[validation.status];
  const Icon = config.icon;

  const statusLabels = {
    pass: t('manager.modules.simulator.preflight.statusOk', {}, 'OK'),
    fail: t('manager.modules.simulator.preflight.statusFail', {}, 'Feil'),
    warning: t('manager.modules.simulator.preflight.statusWarning', {}, 'Advarsel'),
    info: t('manager.modules.simulator.preflight.statusInfo', {}, 'Info')
  };

  return (
    <div className={`px-5 py-4 flex items-center gap-4 ${config.bg} hover:brightness-95 transition-all`}>
      <Icon className={`w-6 h-6 ${config.color} flex-shrink-0`} />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-gray-800">{validation.name}</p>
        <p className="text-sm text-gray-600 truncate">{validation.message}</p>
      </div>
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${config.badge}`}>
        {statusLabels[validation.status]}
      </span>
    </div>
  );
};

// Helper function to group validations by category
function groupByCategory(
  validations: PreFlightValidation[]
): Record<ValidationCategory, PreFlightValidation[]> {
  const groups: Record<ValidationCategory, PreFlightValidation[]> = {
    'KRITISK': [],
    'PÅKREVD': [],
    'ANBEFALT': [],
    'VALGFRITT': []
  };

  for (const validation of validations) {
    groups[validation.category].push(validation);
  }

  return groups;
}
